import type { I18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import {
  agreementFill,
  agreementSearch,
  hireNext,
  payrollBorrowerFill,
  payrollDetailsFill,
  payrollSimulationFill,
} from './simulation-hire.fill';
import { DEBT_PURCHASE_PATH, PUBLIC_REFINANCING_PATH } from './simulation-renewal.screen';
import { summaryContextText, type SummaryAgentState } from './simulation-summary.context';
import { PUBLIC_PAYROLL_PATH, SIMULATION_READ } from './simulation.screen';

// Manifestos dos consignados (U23, U42), arquivo leve para o `src/app/screens.ts`. O caminho é o do legado com o
// rótulo padrão (`formatRoute('Consignado publico')`, labelConfig.ts:8-29): estável, e o rótulo do tenant só no
// texto (inventário, notas do módulo). Herdam `CreditNote:Read` de `/ccb` e só existem com `ENABLE_NEW_SIMULATION === 1`.

export { PUBLIC_PAYROLL_PATH };
export const PUBLIC_PAYROLL_NEW_PATH = `${PUBLIC_PAYROLL_PATH}/novo`;

const NEW_SIMULATION = { flag: { key: 'ENABLE_NEW_SIMULATION', equals: 1 } } as const;

export const simulationPublicPayrollNew = action({
  id: 'simulation.publicPayroll.new',
  kind: 'navigate',
  label: msg`Novo`,
  description: msg`Abre a contratação de um consignado público novo: convênio, pessoa, simulação e a escolha da oferta.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PUBLIC_PAYROLL_NEW_PATH),
});

export const simulationPublicPayrollDebtPurchase = action({
  id: 'simulation.publicPayroll.debtPurchase',
  kind: 'navigate',
  label: msg`Novo + compra de dívida`,
  description: msg`Abre a contratação de consignado público novo com compra de dívida: convênio, CPF e contratos a comprar, simulação e a escolha das ofertas.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(DEBT_PURCHASE_PATH),
});

export const simulationPublicPayrollRefinancing = action({
  id: 'simulation.publicPayroll.refinancing',
  kind: 'navigate',
  label: msg`Refinanciamento`,
  description: msg`Abre o refinanciamento de consignado público: convênio, CPF e contratos a refinanciar, simulação e a escolha da oferta.`,
  permission: SIMULATION_READ,
  run: ({ nav }) => nav.to(PUBLIC_REFINANCING_PATH),
});

export const simulationPublicPayrollScreen = defineScreen({
  id: 'simulation.publicPayroll',
  path: PUBLIC_PAYROLL_PATH,
  title: msg`Consignado público`,
  description: msg`Escolha da jornada de consignado público: Novo, Novo com compra de dívida e Refinanciamento abrem as jornadas de contratação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.hire', flow: true },
  actions: [simulationPublicPayrollNew, simulationPublicPayrollDebtPurchase, simulationPublicPayrollRefinancing],
});

/** Nenhum alvo: as ferramentas agem sobre o fluxo aberto (trava Lite, agent-tool-targets). */
export const payrollSimulateInput = z.strictObject({});
/** A nova parcela (centavos) e a quantidade de parcelas da oferta escolhida; o alvo é o fluxo aberto. */
export const payrollEditProposalInput = z.strictObject({
  installmentValueInCents: z.number().int().positive(),
  numberOfPayments: z.number().int().positive(),
});

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a contratação do consignado para usar esta ação.`);
};

export const publicPayrollActions = {
  simulate: action({
    id: 'simulation.publicPayrollNew.simulate',
    kind: 'write',
    label: msg`Simular ofertas`,
    description: msg`Com o convênio, o CPF e as datas preenchidos, simula as ofertas de todos os produtos do convênio e abre a escolha da oferta. Não cria operação nem consulta margem. Pede a confirmação da pessoa.`,
    keywords: [msg`simular`, msg`ofertas`, msg`consignado`],
    permission: SIMULATION_READ,
    input: payrollSimulateInput,
    preview: () => ({
      title: t`Simular as ofertas?`,
      detail: t`Simula as ofertas dos produtos do convênio. Não cria operação.`,
    }),
    run: mountedScreenRequired,
  }),
  editProposal: action({
    id: 'simulation.publicPayrollNew.editProposal',
    kind: 'write',
    label: msg`Editar proposta`,
    description: msg`Refaz a simulação da oferta escolhida com outro valor de parcela e outra quantidade de parcelas. Não cria operação. Pede a confirmação da pessoa.`,
    keywords: [msg`proposta`, msg`parcela`],
    permission: SIMULATION_READ,
    input: payrollEditProposalInput,
    preview: () => ({
      title: t`Refazer a oferta?`,
      detail: t`Simula de novo a oferta escolhida. Não cria operação.`,
    }),
    run: mountedScreenRequired,
  }),
};

const NEW_PREFIX = 'simulation.publicPayrollNew';

/** O Luy no consignado público novo (U56): busca e escolha do convênio, tomador, simulação, Detalhes e avançar. */
export const publicPayrollFills = {
  searchAgreement: agreementSearch(NEW_PREFIX),
  agreement: agreementFill(NEW_PREFIX),
  borrower: payrollBorrowerFill(NEW_PREFIX),
  simulation: payrollSimulationFill(NEW_PREFIX),
  details: payrollDetailsFill(NEW_PREFIX),
  next: hireNext(NEW_PREFIX),
};

export const simulationPublicPayrollNewScreen = defineScreen({
  id: 'simulation.publicPayrollNew',
  path: PUBLIC_PAYROLL_NEW_PATH,
  title: msg`Novo consignado público`,
  description: msg`Contratação de consignado público em passos: o convênio, o CPF da pessoa, a margem e as datas da simulação e a escolha da oferta de crédito. No Resumo, a conferência do titular e da conta e o envio para análise, que cria a operação.`,
  permission: SIMULATION_READ,
  access: NEW_SIMULATION,
  place: { icon: 'file-text', parent: 'simulation.publicPayroll', flow: true },
  actions: [publicPayrollActions.simulate, publicPayrollActions.editProposal, ...Object.values(publicPayrollFills)],
});

/** Contexto do hub do consignado público (U46): a etapa à vista, o produto já escolhido e a modalidade marcada. */
export function publicPayrollHubContext(i18n: I18n, chosen?: string | null): ScreenContext {
  const modality = chosen ?? '';
  return {
    summary: [
      i18n._(msg`Contratar consignado público, etapa 2: Modalidade. O produto já foi escolhido.`),
      i18n._(
        msg`Escolha da jornada de consignado público. Novo, Novo com compra de dívida e Refinanciamento abrem as jornadas de contratação.`,
      ),
      modality ? i18n._(msg`Modalidade marcada: ${modality}.`) : '',
    ]
      .filter(Boolean)
      .join(' '),
    fields: { step: 'modalidade', modalityChosen: modality !== '' },
    sensitivity: 'internal',
  };
}

export interface PayrollFlowContextInput {
  readonly step: string;
  readonly stepTitle: string;
  readonly agreement: string | null;
  readonly marginQueried: boolean;
  readonly offers: number | null;
  readonly selected: boolean;
  /** Só no Resumo: se a pessoa e a conta estão prontas para o envio. */
  readonly summary?: SummaryAgentState | null;
}

/** O que o agente lê do fluxo (U24, U46): o passo, o convênio e as ofertas. Sem CPF nem valores da margem. */
export function payrollFlowContext(input: PayrollFlowContextInput, i18n: I18n): ScreenContext {
  const step = input.stepTitle;
  const agreement = input.agreement;
  const offers = input.offers;
  const parts = [
    i18n._(msg`Contratação de consignado público.`),
    i18n._(msg`Passo atual: ${step}.`),
    agreement ? i18n._(msg`Convênio escolhido: ${agreement}.`) : '',
    input.marginQueried ? i18n._(msg`A margem livre foi consultada.`) : '',
    offers === null
      ? ''
      : offers > 0
        ? i18n._(msg`Há ${offers} ofertas para escolher.`)
        : i18n._(msg`Não há ofertas.`),
    input.selected ? i18n._(msg`Uma oferta está escolhida.`) : '',
    summaryContextText(input.summary, i18n),
  ];
  return {
    summary: parts.filter(Boolean).join(' '),
    fields: {
      step: input.step,
      agreementChosen: Boolean(agreement),
      marginQueried: input.marginQueried,
      offers,
      selected: input.selected,
      ...(input.summary ? { personFound: input.summary.personFound, bankAccount: input.summary.bankAccount } : {}),
    },
    sensitivity: 'internal',
  };
}
